import { Accordion, Spinner, useAccordionItemContext } from '@chakra-ui/react';
import { ICreditNoteSchema, IInvoiceRespSchema } from '@monetize/types/app';
import { formatCurrency, toDateShort } from '@monetize/utils/core';
import React, {
  FunctionComponent as FC,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { doGetCreditNotesForInvoice } from '../../api/accountsService';
import { handleApiErrorToast } from '../../api/axios';
import {
  MAccordionCustomButton,
  MAccordionCustomButtonItem,
  MAccordionPanel,
  MFlex,
  MStack,
  MText,
} from '../../components/Monetize';
import { PAPER_PAGE_MAX_WIDTH } from '../../constants/quotes';

export interface InvoiceCreditNotesProps {
  invoice: IInvoiceRespSchema;
}

interface CreditNoteAccordionItemBodyProps {
  createDate: string;
  amount: number | string;
  refundable: boolean;
  description: React.ReactNode;
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
const CreditNoteAccordionItemBody = ({
  createDate,
  amount,
  refundable,
  description,
}: CreditNoteAccordionItemBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton
        isExpanded={isExpanded}
        label={toDateShort(createDate)}
      >
        <MAccordionCustomButtonItem
          align="right"
          label="Amount:"
          value={formatCurrency(amount)}
          isExpanded={isExpanded}
          hideWhenExpanded
        />
      </MAccordionCustomButton>
      {/* MAccordionPanel puts `pr` on the padded body, as v2's
          `AccordionPanel pr={8}` did; on ItemContent it stacked on the
          body's own side padding. */}
      <MAccordionPanel pr={8}>
        <MFlex justifyContent="space-between">
          <MStack gap={24} direction="row">
            <StackedLabelValue
              align="left"
              label="Date"
              value={toDateShort(createDate)}
            />
            <StackedLabelValue
              align="left"
              label="Refundable"
              value={refundable ? 'Yes' : 'No'}
            />
            <StackedLabelValue
              align="left"
              label="Comments"
              value={description}
            />
          </MStack>
          <StackedLabelValue
            align="right"
            label="Amount"
            value={formatCurrency(+amount)}
          />
        </MFlex>
      </MAccordionPanel>
    </>
  );
};

const InvoiceCreditNotes: FC<InvoiceCreditNotesProps> = (
  props: InvoiceCreditNotesProps,
) => {
  const { invoice } = props;

  const [loading, setLoading] = useState(false);
  const [creditNotes, setCreditNotes] = useState<ICreditNoteSchema[]>([]);

  const getCreditNotes = useCallback(async () => {
    try {
      setLoading(true);
      const creditNotesResponse = await doGetCreditNotesForInvoice(invoice.id);
      setCreditNotes(creditNotesResponse.content);
    } catch (error) {
      handleApiErrorToast(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (invoice) {
      getCreditNotes();
    }
  }, [invoice]);

  return (
    <MStack w="100%" maxWidth={PAPER_PAGE_MAX_WIDTH}>
      {creditNotes.length && (
        <MText as="h2" fontWeight="bold" fontSize="lg" mt={8} mb={2}>
          Credit Notes
          {loading && <Spinner size="xs" ml={2} />}
        </MText>
      )}
      <Accordion.Root multiple w="100%" mt={4}>
        {creditNotes.map(
          ({ amount, id, refundable, createDate, description }, index) => (
            <Accordion.Item
              key={id}
              value={String(index)}
              mt={4}
              borderWidth="1px"
              borderRadius="md"
              borderColor="tGray.back"
            >
              <CreditNoteAccordionItemBody
                createDate={createDate}
                amount={amount}
                refundable={refundable}
                description={description}
              />
            </Accordion.Item>
          ),
        )}
      </Accordion.Root>
    </MStack>
  );
};

interface StackedLabelValueProps {
  label: React.ReactNode;
  value: React.ReactNode;
  align?: 'left' | 'right';
}

const StackedLabelValue: FC<StackedLabelValueProps> = ({
  label,
  value,
  align = 'right',
}: StackedLabelValueProps) => {
  return (
    <MStack gap="0px">
      <MText py={0} m={0} fontWeight="bold" textAlign={align} fontSize="md">
        {label}
      </MText>{' '}
      <MText py={0} m={0} textAlign={align} fontSize="sm">
        {value}
      </MText>
    </MStack>
  );
};

export default InvoiceCreditNotes;
